/**
 * Google Calendar Connection Component
 * Allows users to connect/disconnect their Google Calendar
 */
import React from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Calendar, Check, X, Loader2 } from "lucide-react";

export function CalendarConnection() {
  const { token, user, updateUser } = useAuth();
  const [loading, setLoading] = React.useState(false);
  const [status, setStatus] = React.useState(null);

  // Check for callback result in URL
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const calendarResult = params.get("calendar");
    
    if (calendarResult === "connected") {
      toast.success("Google Calendar connected successfully!");
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
      fetchStatus();
    } else if (calendarResult === "error") {
      toast.error("Failed to connect Google Calendar");
      window.history.replaceState({}, "", window.location.pathname);
      fetchStatus(); // Also fetch status on error to show current state
    }
  }, []);

  // Fetch current status
  const fetchStatus = React.useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API}/oauth/calendar/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (e) {
      console.error("Failed to fetch calendar status:", e);
    }
  }, [token]);

  React.useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleConnect = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/oauth/calendar/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        // Redirect to Google OAuth
        window.location.href = data.authorization_url;
      } else {
        toast.error("Failed to start calendar connection");
      }
    } catch (e) {
      toast.error("Failed to connect calendar");
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/oauth/calendar/disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setStatus({ connected: false });
        toast.success("Calendar disconnected");
      } else {
        toast.error("Failed to disconnect calendar");
      }
    } catch (e) {
      toast.error("Failed to disconnect calendar");
    } finally {
      setLoading(false);
    }
  };

  const isConnected = status?.connected;

  return (
    <Card className="bg-white/[0.03] border-white/10 p-6">
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-lg ${isConnected ? "bg-[#ff6b6b]/20" : "bg-white/[0.05]"}`}>
          <Calendar className={`w-6 h-6 ${isConnected ? "text-[#ff6b6b]" : "text-white/60"}`} />
        </div>
        
        <div className="flex-1">
          <h3 className="font-bold text-lg mb-1">Google Calendar</h3>
          
          {isConnected ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-[#ff6b6b]">
                <Check className="w-4 h-4" />
                <span className="text-sm">Connected</span>
              </div>
              {status?.google_email && (
                <p className="text-white/50 text-sm">{status.google_email}</p>
              )}
              <p className="text-white/40 text-xs">
                Your availability will be shown to studios when they view candidates for jobs.
              </p>
            </div>
          ) : (
            <p className="text-white/50 text-sm">
              Connect your calendar to show studios when you're available. 
              Jobs that conflict with your schedule will show you as unavailable.
            </p>
          )}
        </div>
        
        <div>
          {isConnected ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDisconnect}
              disabled={loading}
              className="border-[#ff6b6b]/30 text-[#ff6b6b] hover:bg-[#ff6b6b]/10"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4 mr-1" />}
              Disconnect
            </Button>
          ) : (
            <Button
              onClick={handleConnect}
              disabled={loading}
              className="bg-white text-black hover:bg-gray-200"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Calendar className="w-4 h-4 mr-2" />
              )}
              Connect Calendar
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

export default CalendarConnection;
